// C9 — Collaboration API client.
//
// Wraps the C4–C5 endpoints with one consistent signature. Each call
// returns the parsed JSON body on 2xx, throws an Error with a
// `.code` property on error (mapped from `detail.code` in the backend
// error envelope) so callers can branch on machine-readable codes:
//
//     try { await collabApi.invite('design', id, {email, role}); }
//     catch (e) {
//       if (e.code === 'invite_duplicate') ...
//     }
//
// `kind` is "design" or "template" (singular). The path uses the plural.

import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const AUTH_TOKEN_KEY = 'ivisora_auth_token';
const DESIGNER_TOKEN_KEY = 'user_token';

const getToken = () =>
  localStorage.getItem(AUTH_TOKEN_KEY) || localStorage.getItem(DESIGNER_TOKEN_KEY);

const authHeaders = () => ({
  'Content-Type': 'application/json',
  Authorization: `Bearer ${getToken()}`,
});

const path = (kind, id, ...rest) =>
  `${API_URL}/api/${kind}s/${id}/${rest.join('/')}`.replace(/\/+$/, '');

function unwrapError(err) {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;

  // Structured detail (the C-series convention): { code, message, ... }.
  if (detail && typeof detail === 'object') {
    const e = new Error(detail.message || 'Request failed');
    e.code = detail.code;
    e.status = status;
    e.detail = detail;
    return e;
  }

  // String detail (FastAPI default for framework-level errors like 401
  // from get_current_user — "Not authenticated"). Map by HTTP status so
  // users get friendly copy instead of "Request failed with status code 401".
  const friendly = friendlyByStatus(status);
  const message = friendly?.message
    || (typeof detail === 'string' ? detail : err?.message)
    || 'Request failed';
  const e = new Error(message);
  e.code = friendly?.code || null;
  e.status = status;
  return e;
}

// Maps HTTP status → user-facing copy + machine-readable code. Used when
// the backend returns string detail (or no detail at all), most commonly
// for 401 from auth middleware. Routes that emit structured detail (our
// own /invite, /role, etc.) keep their specific codes and bypass this.
function friendlyByStatus(status) {
  switch (status) {
    case 401:
      return { code: 'session_expired',
               message: 'Your session has expired. Please sign in again.' };
    case 403:
      return { code: 'forbidden',
               message: "You don't have permission to do that." };
    case 404:
      return { code: 'not_found',
               message: 'We couldn’t find what you were looking for.' };
    case 429:
      return { code: 'too_many_requests',
               message: 'Too many requests — please slow down.' };
    case 500:
    case 502:
    case 503:
    case 504:
      return { code: 'server_error',
               message: "Something went wrong on our end. Try again in a moment." };
    default:
      return null;
  }
}

async function call(method, url, body) {
  try {
    const res = await axios.request({
      method, url, data: body, headers: authHeaders(),
    });
    return res.data;
  } catch (err) {
    throw unwrapError(err);
  }
}

export const collabApi = {
  list: (kind, id) =>
    call('get', path(kind, id, 'collaborators')),

  invite: (kind, id, { email, role }) =>
    call('post', path(kind, id, 'invite'), { email, role }),

  changeRole: (kind, id, cid, role) =>
    call('patch', path(kind, id, 'collaborators', cid, 'role'), { role }),

  remove: (kind, id, cid) =>
    call('delete', path(kind, id, 'collaborators', cid)),

  resend: (kind, id, cid) =>
    call('post', path(kind, id, 'invite', cid, 'resend'), { send_email: true }),

  selfLeave: (kind, id) =>
    call('delete', path(kind, id, 'collaborators', 'me')),
};

// User-friendly copy mapping. UI components import this so error
// messages stay consistent across surfaces. Includes both backend-
// structured codes (C-series) AND the synthetic codes that friendlyByStatus
// emits for framework-level errors (401 / 403 / 429 / 5xx).
export const ERROR_COPY = {
  // Auth (synthetic, from friendlyByStatus)
  session_expired: 'Your session has expired. Please sign in again.',
  forbidden: "You don't have permission to invite collaborators.",
  not_found: 'We couldn’t find this resource.',
  too_many_requests: 'Too many requests — please slow down.',
  server_error: "Something went wrong on our end. Try again in a moment.",
  // Invite-specific (backend structured detail)
  invite_self: "That's your own email.",
  invite_owner_email: 'This email belongs to the resource owner.',
  invite_duplicate: 'This email is already invited.',
  invite_rate_limit_hour: "You've sent too many invitations in the last hour.",
  invite_rate_limit_day: "You've sent too many invitations today.",
  not_authorized: "You don't have permission to invite collaborators.",
  role_change_not_allowed: 'Role can only be changed on accepted collaborators.',
  role_unchanged: 'That role is already set.',
  already_inactive: 'This collaborator was already removed.',
  cannot_remove_owner: "The resource owner can't be removed.",
  owner_cannot_leave: "You can't leave a resource you own.",
  not_a_collaborator: "You're not a collaborator on this resource.",
  resend_not_pending: 'Only pending invitations can be resent.',
  access_invalid: 'Pick Private or Anyone with Link.',
};
